import { z } from 'zod';
/**
 * @atlas/contracts — HubSpot integration (Support escalation ↔ Dev).
 *
 * "New system" in the product spec is Tracx. HubSpot custom properties use the
 * `tracx_*` namespace. Comments are the Message model; files are the Attachment
 * model; audit is the shared AuditEvent. These schemas validate the admin config,
 * mappings, link/sync operations, and the ticket-detail panel DTO.
 */
// --- Connection + settings ---------------------------------------------------
/** Masked connection config returned to the client — never includes secrets. */
export const HubspotConnectionConfig = z.object({
    enabled: z.boolean(),
    portalId: z.string().nullable(),
    baseUrl: z.string(),
    authType: z.string(),
    hasToken: z.boolean(),
    tokenHint: z.string().nullable(),
    hasWebhookSecret: z.boolean(),
    webhookSecretHint: z.string().nullable(),
    status: z.string(),
    statusNote: z.string().nullable(),
    lastTestedAt: z.string().nullable(),
    // Escalation
    supportPipelineId: z.string().nullable(),
    escalationStageId: z.string().nullable(),
    escalationProperty: z.string(),
    escalationValue: z.string(),
    defaultProjectId: z.string().nullable(),
    defaultTicketType: z.string(),
    defaultStatus: z.string(),
    defaultAssigneeId: z.string().nullable(),
    watchers: z.array(z.string()),
    // Comment sync
    inboundCommentsEnabled: z.boolean(),
    outboundCommentsEnabled: z.boolean(),
    defaultCommentVisibility: z.string(),
    allowCustomerVisibleSync: z.boolean(),
    syncSystemComments: z.boolean(),
    syncStatusNotes: z.boolean(),
    // File sync
    fileSyncEnabled: z.boolean(),
    inlineImageSyncEnabled: z.boolean(),
    maxFileBytes: z.number(),
    allowedFileTypes: z.array(z.string()),
    blockedFileTypes: z.array(z.string()),
    virusScanRequired: z.boolean(),
    storeFilesPermanently: z.boolean(),
    // Status sync
    autoCloseHubspotOnDone: z.boolean(),
    // Retry / polling
    maxRetries: z.number(),
    notePollMinutes: z.number(),
    updatedAt: z.string().nullable(),
});
/** Admin settings update. Omitted token/secret keeps the stored value. */
export const UpdateHubspotConnectionInput = z.object({
    enabled: z.boolean().optional(),
    portalId: z.string().optional(),
    baseUrl: z.string().url().optional(),
    authType: z.enum(['private_app', 'oauth']).optional(),
    token: z.string().optional(), // private-app token; empty/omitted keeps existing
    webhookSecret: z.string().optional(), // HubSpot app client secret; empty/omitted keeps existing
    supportPipelineId: z.string().optional(),
    escalationStageId: z.string().optional(),
    escalationProperty: z.string().optional(),
    escalationValue: z.string().optional(),
    defaultProjectId: z.string().optional(),
    defaultTicketType: z.string().optional(),
    defaultStatus: z.string().optional(),
    defaultAssigneeId: z.string().nullable().optional(),
    watchers: z.array(z.string()).optional(),
    inboundCommentsEnabled: z.boolean().optional(),
    outboundCommentsEnabled: z.boolean().optional(),
    defaultCommentVisibility: z.enum(['internal', 'agent_only', 'private_admin']).optional(),
    allowCustomerVisibleSync: z.boolean().optional(),
    syncSystemComments: z.boolean().optional(),
    syncStatusNotes: z.boolean().optional(),
    fileSyncEnabled: z.boolean().optional(),
    inlineImageSyncEnabled: z.boolean().optional(),
    maxFileBytes: z.number().int().positive().optional(),
    allowedFileTypes: z.array(z.string()).optional(),
    blockedFileTypes: z.array(z.string()).optional(),
    virusScanRequired: z.boolean().optional(),
    storeFilesPermanently: z.boolean().optional(),
    autoCloseHubspotOnDone: z.boolean().optional(),
    maxRetries: z.number().int().min(0).max(20).optional(),
    notePollMinutes: z.number().int().min(1).max(60).optional(),
});
export const HubspotTestInput = z
    .object({ token: z.string().optional(), baseUrl: z.string().url().optional() })
    .optional();
// --- Mappings ----------------------------------------------------------------
export const HubspotMappingType = z.enum(['hubspot_to_tracx', 'tracx_to_hubspot']);
export const HubspotTransitionMode = z.enum([
    'mirror_field_only',
    'transition_workflow',
    'add_note_only',
]);
export const UpsertStatusMappingInput = z.object({
    id: z.string().optional(),
    mappingType: HubspotMappingType,
    sourceStatusId: z.string().min(1),
    sourceStatusLabel: z.string().optional(),
    targetStatusId: z.string().min(1),
    targetStatusLabel: z.string().optional(),
    transitionMode: HubspotTransitionMode.optional(),
    enabled: z.boolean().optional(),
});
export const UpsertFieldMappingInput = z.object({
    id: z.string().optional(),
    direction: HubspotMappingType,
    sourceField: z.string().min(1),
    targetField: z.string().min(1),
    transformRule: z.unknown().optional(),
    required: z.boolean().optional(),
    defaultValue: z.string().optional(),
    enabled: z.boolean().optional(),
});
export const UpsertUserMappingInput = z.object({
    id: z.string().optional(),
    sourceSystem: z.enum(['hubspot', 'tracx']),
    sourceUserId: z.string().min(1),
    sourceUserEmail: z.string().optional(),
    sourceDisplayName: z.string().optional(),
    tracxUserId: z.string().nullable().optional(),
    mappingStatus: z.enum(['mapped', 'unmapped', 'ignored']).optional(),
});
/** Manually link a Tracx ticket to a HubSpot ticket (§14.3). */
export const LinkHubspotTicketInput = z.object({
    hubspotTicketId: z.string().min(1),
    hubspotPortalId: z.string().optional(),
    hubspotTicketUrl: z.string().url().optional(),
});
/** Sync a Tracx comment out to HubSpot on demand (§14.3). */
export const SyncCommentInput = z.object({
    messageId: z.string().min(1),
    customerVisible: z.boolean().optional(),
});
//# sourceMappingURL=hubspot.js.map